import { Link } from 'react-router';
import { BlogPostLayout } from '@/components/blog/blog-post-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

const quoteClass = 'border-l-4 border-accent-500 bg-accent-50 rounded-r-2xl p-5 my-8';

export function SummerRoadTripsPotholeDamageBlogPost() {
  return (
    <BlogPostLayout
      slug="summer-road-trips-pothole-damage"
      title="Summer Road Trips and Pothole Damage: What to Check Before You Drive Away"
      description="Before a summer road trip, check for pothole damage that can become dangerous under heat, load, and motorway speeds. Use this tyre, suspension, and evidence checklist."
      category="Pothole Claim Advice"
      readTime="6 min"
      datePublished="2026-06-14"
      dateModified="2026-06-14"
      image={{
        src: '/blog/summer-road-trips-pothole-damage-1200.webp',
        srcSet:
          '/blog/summer-road-trips-pothole-damage-800.webp 800w, /blog/summer-road-trips-pothole-damage-1200.webp 1200w',
        sizes: '(min-width: 1024px) 896px, calc(100vw - 32px)',
        alt: 'Driver checking a front tyre before a summer road trip with luggage loaded in the boot.',
        width: 1200,
        height: 630,
        caption:
          'A quick tyre, sidewall and suspension check before a summer road trip can catch pothole damage before it becomes a roadside problem.',
      }}
      keywords={[
        'summer road trip car check',
        'pothole damage before long drive',
        'tyre sidewall bulge',
        'loaded car tyre pressure',
        'pothole damage claim',
      ]}
      geoFocus={['England', 'Wales', 'UK motorways', 'UK local roads']}
      atAGlance={[
        { label: 'Main risk', value: 'Minor pothole damage can fail under heat, luggage weight, passengers, and motorway speeds.' },
        { label: 'Fastest check', value: 'Look at tyre sidewalls, laden pressures, steering pull, suspension knocks, underside damage, and spare kit.' },
        { label: 'If you find damage', value: 'Photograph it before repair and ask the mechanic to note whether it is consistent with impact.' },
        { label: 'Best timing', value: 'Check before you leave, not after the long drive exposes the weakness.' },
      ]}
      sources={[
        { name: 'Highways Act 1980, Section 41', url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/41' },
        { name: 'DVSA MOT inspection manual: tyres', url: 'https://www.gov.uk/guidance/mot-inspection-manual-for-private-passenger-and-light-commercial-vehicles/5-axles-wheels-tyres-and-suspension' },
        { name: 'Your Local Mechanic', url: 'https://yourlocalmechanic.co.uk' },
      ]}
      faqs={[
        {
          question: 'What pothole damage should I check before a summer road trip?',
          answer:
            'Check tyre sidewalls for bulges or cracks, set tyre pressures for a loaded car, listen for suspension knocks, check whether the car pulls to one side, look underneath for impact damage, and make sure the spare or repair kit is usable.',
        },
        {
          question: 'Why can pothole damage become worse on a long summer drive?',
          answer:
            'A fully loaded car, motorway speeds, longer distances, and high temperatures all put extra strain on tyres, suspension, steering, and alignment. Weaknesses that seemed minor locally can fail on a long trip.',
        },
        {
          question: 'Can I claim for pothole damage found before a road trip?',
          answer:
            'Potentially, yes. If the damage can reasonably be linked to a pothole impact, photograph it before repair, record what you remember about the incident, and ask the mechanic to note whether the damage is consistent with impact rather than ordinary wear.',
        },
      ]}
      relatedPosts={[
        {
          slug: 'mechanics-view-hidden-pothole-damage',
          title: "The Mechanic's View",
          description: 'A qualified mechanic explains the hidden pothole damage drivers miss.',
          readTime: '7 min',
        },
        {
          slug: 'spring-pothole-season',
          title: 'Spring Pothole Season',
          description: 'Why winter pothole damage can show up weeks later.',
          readTime: '7 min',
        },
      ]}
    >
      <p>
        Summer is when the car gets loaded up and pointed at the coast, the campsite or the motorway
        for a few hundred miles. It is also when minor, ignored pothole damage picks its moment to
        become a roadside emergency. A worn shock or a weakened tyre that coped fine with the school
        run behaves very differently fully loaded, at speed, on a long drive in the heat.
      </p>

      <p>
        The good news is that a ten-minute check before you set off catches most of it. Here is what
        to look at, why it matters for a long trip, and what to do if you find damage that traces
        back to a pothole.
      </p>

      <h2>Why Summer Exposes Pothole Damage</h2>

      <p>
        A car carrying four people and a boot full of luggage is under far more strain than the same
        car on a short local trip. Add motorway speeds, longer distances and high summer
        temperatures, and any existing weakness gets tested hard.
      </p>

      <p>
        Two things make pothole damage especially likely to surface now. First, the roads took their
        beating over winter and early spring, so by summer there is a backlog of cars carrying damage
        their owners never dealt with. Second, the failures that pothole damage causes, tyre
        blowouts, suspension giving way, alignment problems, are exactly the failures that a hot,
        heavy, high-speed drive brings to a head. The pre-trip check exists to find them in your
        driveway rather than on the hard shoulder.
      </p>

      <h2>Your Pre-Trip Pothole Check</h2>

      <p>You do not need tools or expertise for most of this. You need ten minutes and a bit of attention.</p>

      <ul>
        <li>
          <strong>Tyres, including the sidewalls.</strong> Check tread depth and pressure, but do not
          stop at the tread. Run your eye around the sidewall of each tyre, the part facing outward,
          looking for bulges, blisters or cracks. A bulge is a weak spot that can fail under the heat
          and load of a long drive. This is the damage pothole impacts most often leave behind, and
          the part drivers most often miss.
        </li>
        <li>
          <strong>Pressures for a loaded car.</strong> A fully loaded car usually needs higher tyre
          pressures than a near-empty one. Check your handbook or the sticker in the door frame for
          the laden figure, and set them before you go. Correct pressures matter even more if a tyre
          is already carrying minor damage.
        </li>
        <li>
          <strong>The way the car sits and steers.</strong> On a slow test drive around the block,
          notice whether the car pulls to one side, whether the steering wheel sits straight when you
          are going straight ahead, and whether there are any knocks or clunks over bumps. All three
          can be signs of pothole damage to alignment or suspension, and all three get worse, not
          better, over a long drive.
        </li>
        <li>
          <strong>A look underneath.</strong> A quick glance under the front of the car, especially
          on lower vehicles, can reveal a hanging undertray, fresh scrapes or signs of an impact you
          had forgotten about.
        </li>
        <li>
          <strong>The spare and the kit.</strong> Make sure you actually have a usable spare or a
          working tyre repair kit, and that you know where it is. If pothole damage does strike on
          the road, this is the difference between a delay and a ruined day.
        </li>
      </ul>

      <h2>A Mechanic's Pre-Trip Tip</h2>

      <p>The one check drivers most often skip is the one a mechanic would do first.</p>

      <blockquote className={quoteClass}>
        <p className="text-brand-950 font-medium">
          "Before any long drive, walk around the car and properly look at the sidewalls, not just
          the tread," says Elliot Wheeler, a qualified mechanic with over 15 years' experience. "A
          pothole bruise on the sidewall might have sat there for weeks doing nothing on short trips.
          Load the car up, get it hot on a motorway for three hours, and that's exactly when it lets
          go. Two minutes looking before you leave can save you a blowout at seventy."
        </p>
      </blockquote>

      <p>
        For more on why sidewall, steering, and suspension damage is so easy to miss, read{' '}
        <Link to="/blog/mechanics-view-hidden-pothole-damage" className={inlineLink}>
          The Mechanic's View
        </Link>
        .
      </p>

      <h2>What to Do if You Find Pothole Damage</h2>

      <p>
        If your pre-trip check turns up damage, and you can reasonably trace it to a pothole you
        remember hitting, do not just fix it and forget it. That damage may be claimable.
      </p>

      <p>Before any repair:</p>

      <ul>
        <li>Photograph the damage clearly, from more than one angle.</li>
        <li>Note what you remember about the pothole: roughly when and where you hit it, and how hard.</li>
        <li>
          Ask the mechanic doing the repair to note in writing whether the damage is consistent with
          an impact rather than ordinary wear.
        </li>
      </ul>

      <p>
        You have a legal right to claim for pothole damage under the{' '}
        <Link to="/guides/section-41-explained" className={inlineLink}>
          Highways Act 1980
        </Link>
        , and the fact that you spotted it during a pre-trip check rather than at the moment of
        impact does not remove that right. What matters is linking the damage to the pothole, which
        is why the photos and the mechanic's note are worth gathering before the evidence disappears
        under a repair.
      </p>

      <p>
        Fixtyer's guides to{' '}
        <Link to="/guides/documenting-vehicle-damage" className={inlineLink}>
          documenting vehicle damage
        </Link>{' '}
        and{' '}
        <Link to="/guides/getting-repair-quotes" className={inlineLink}>
          getting the right repair quote
        </Link>{' '}
        explain how to turn a repair into claim-ready evidence.
      </p>

      <h2>Do Not Let a Small Job Become a Big One</h2>

      <p>
        There is a practical reason to deal with pothole damage before a trip rather than after,
        beyond the claim. A weakened tyre or a tired shock that fails at home is an inconvenience.
        The same failure at speed, fully loaded, with passengers aboard, is a genuine hazard. If your
        check finds something that looks marginal, the safe call is to get it sorted before you go,
        not to gamble on it lasting the journey.
      </p>

      <h2>The Bottom Line</h2>

      <p>
        Summer road trips put cars under exactly the kind of strain that exposes hidden pothole
        damage. A ten-minute check before you set off, tyres and sidewalls, pressures for a loaded
        car, steering and suspension feel, a glance underneath, finds the problems while you can
        still do something about them. And if that check turns up damage from a pothole you remember,
        photograph it and get it noted before you repair it, because a check that protects your trip
        can protect your right to claim the cost back too.
      </p>

      <div className="bg-brand-50 border border-brand-200 rounded-2xl p-6">
        <p className="text-sm text-surface-700 leading-relaxed">
          Fixtyer helps UK drivers prepare and submit their own pothole damage claims, without
          solicitors and without commission, so you keep 100% of any compensation. Fixtyer provides
          documents and guidance only and does not provide legal advice. If you are unsure about your
          individual circumstances, seek independent legal advice.
        </p>
      </div>
    </BlogPostLayout>
  );
}
